import { Action, Role, VoiceChatMessage } from "@/lib/messaging";
import { getModeByName, Modes } from "@/lib/modes";
import { useEffect, useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { CarouselModeComponent } from "@/components/pages/voicechat/carousel-mode";
import { KeyHelper } from "@/components/common/key-helper";
import { MicButtonComponent } from "@/components/pages/voicechat/mic-button";
import { ModeContextCard } from "@/components/pages/voicechat/mode-context";
import { ModeSidePanelComponent } from "@/components/pages/voicechat/mode-side-panel";
import TooltipTextTranslate from "@/components/pages/voicechat/tooltip-text-translate";
import { TranslationPanel } from "@/components/pages/voicechat/translation-panel";
import { getLanguageByKey } from "@/components/language/supported-languages";
import { toast } from "sonner";
import useAudioInput from "@/hooks/audioInput";
import useAudioTimer from "@/hooks/audioTimer";
import { CompleteOnboardingTour } from "@/client/core";
import {
  useLearnLanguage,
  type LanguageProficiency,
} from "@/context/learn-language-context";
import useMessaging from "@/hooks/messaging";
import { usePocketBase } from "@/context/pocketbase-context";
import { useTranslation } from "react-i18next";
import { useVoiceChatReducer } from "@/hooks/useVoiceChatReducer";
import { useWebSocketMessages } from "@/hooks/useWebSocketMessages";
import { ErrorFetchingData } from "@/components/common/user-messages";
import { VoicechatSkeleton } from "@/components/pages/voicechat/voicechat-skeleton";
import { useVoiceChatTour } from "@/hooks/useVoiceChatTour";

export default function VoiceChatPage() {
  const { languageProficiency, isLoading, isError } = useLearnLanguage();

  // Guard to prevent component rendering if languageProficiency is not properly loaded
  if (isLoading && !languageProficiency) {
    return <VoicechatSkeleton />;
  }

  if (isError || (!isLoading && !languageProficiency)) {
    return <ErrorFetchingData />;
  }

  return <VoiceChatPageContent languageProficiency={languageProficiency!} />;
}

function VoiceChatPageContent({
  languageProficiency,
}: {
  languageProficiency: LanguageProficiency;
}) {
  // Contexts
  const { session, user, token } = usePocketBase();
  const { buildMessage } = useMessaging();
  const { i18n, t } = useTranslation(["pages", "common"]);

  const [searchParams, setSearchParams] = useSearchParams();

  // Determine initial mode from URL ?mode= param, falling back to LangCoach
  const initialMode = (() => {
    const modeStr = searchParams.get("mode");
    return modeStr
      ? (getModeByName(modeStr) ?? Modes.LangCoach)
      : Modes.LangCoach;
  })();

  // state should be the single source of truth for messages
  // (still pending to refactor audio state and websocket state to the reducer)
  // Use reducer functions to update state based on incoming messages, mode changes, and user actions
  const {
    state: voiceChatState,
    selectMode: dispatchSelectMode,
    setModeSpeakingStarted,
    setModeContext,
    setCurrentMessage,
    setTranslationMessage,
    toggleTranslationPanel,
    reset: resetVoiceChatState,
  } = useVoiceChatReducer(initialMode);

  // Wraps selectMode to keep the URL ?mode= param in sync
  const selectMode = useCallback(
    (mode: Parameters<typeof dispatchSelectMode>[0]) => {
      dispatchSelectMode(mode);
      setSearchParams({ mode: mode.modeStr });
    },
    [dispatchSelectMode, setSearchParams],
  );

  // Sync state when user navigates back/forward
  useEffect(() => {
    const modeStr = searchParams.get("mode");
    if (!modeStr) return;
    const mode = getModeByName(modeStr);
    if (mode && mode.modeStr !== voiceChatState.mode.modeStr) {
      dispatchSelectMode(mode);
    }
  }, [searchParams, dispatchSelectMode, voiceChatState.mode.modeStr]);

  // Set URL param on initial load if absent (intentionally mount-only)
  useEffect(() => {
    if (!searchParams.get("mode")) {
      setSearchParams({ mode: voiceChatState.mode.modeStr }, { replace: true });
    }
  }, []);

  // Local state (cannot be in reducer)
  const [isMuted, setIsMuted] = useState(false);
  const [isResponsePlaying, setIsResponsePlaying] = useState(false);

  // ------------  Process inbound message -----------
  // Setup Websocket and handle message received

  // Extracts information from received message and set state accordingly
  const { websocketRef, sendWebsocketMessage } = useWebSocketMessages(
    voiceChatState.mode,
    setCurrentMessage,
    setTranslationMessage,
    setModeContext,
  );

  // ------------  Tour -----------
  const { startTour } = useVoiceChatTour();

  useEffect(() => {
    if (
      !user ||
      !token ||
      user.flags?.some((flag) => flag.key === "showed_voice_chat_tour")
    ) {
      return;
    }
    async function completeTour() {
      try {
        await CompleteOnboardingTour(user!.id, token);
      } catch (error) {
        console.error("Failed to mark tour as completed:", error);
      }
    }
    startTour(() => void completeTour());
  }, [startTour, user, token]);

  // ------------  Process audio input -----------
  const {
    transcriptState: transcript,
    interimTranscript,
    isAudioDetected,
    newAudio,
    audioReadyCounter,
    accumulatedAudio,
    isLoading,
    setAudioAccumulation,
    pause,
    start,
    clear,
  } = useAudioInput({
    autoStart: !isMuted,
    disableRecording: voiceChatState.translationPanel.isOpen, // Disable recording when translation panel is open
    languageProp: languageProficiency.language,
    onSpeechStart: () => {
      if (voiceChatState.translationPanel.isOpen) {
        setModeSpeakingStarted(Modes.Translate);
        setAudioAccumulation(false);
      } else {
        setModeSpeakingStarted(voiceChatState.mode);
      }
    },
    onVADMisfire: () => {
      // Audio detected but not long enough to be considered human speak
      console.log("VAD misfire detected");
      resetAudioTimer(); // Reset audio timer when VAD misfires
    },
  });

  // -------------- Audio Timer ----------------
  const { speakingDuration, resetAudioTimer, stopTimer } = useAudioTimer({
    audioDetected: isAudioDetected,
  });

  useEffect(() => {
    if (isResponsePlaying) {
      resetAudioTimer(); // Reset audio timer when response starts playing
      pause("all");
    } else if (!isMuted) {
      start("all");
    }
  }, [isResponsePlaying]);

  // Voice processing and send message when user stops talking
  useEffect(() => {
    const recordingDuration = stopTimer(); // Stop audio timer when user stops talking
    if (isMuted && !voiceChatState.translationPanel.isOpen) return;

    // newAudio changes also when we clear the state
    if (newAudio.length <= 0 && accumulatedAudio.length <= 0) return;

    if (newAudio.length <= 0) return;

    const message = buildMessage(
      Role.User,
      Action.Message,
      voiceChatState.translationPanel.isOpen
        ? Modes.Translate.modeStr
        : voiceChatState.mode.modeStr,
      voiceChatState.translationPanel.isOpen
        ? getLanguageByKey(i18n.language).name
        : undefined,
      i18n.language,
    );

    // Translation panel is open - client-side transcription + REST API handles translation
    if (voiceChatState.translationPanel.isOpen) {
      resetAudioTimer();
      return;
    }

    // Send accumulated audio if the translation panel is closed
    if (
      !voiceChatState.translationPanel.isOpen &&
      voiceChatState.modeSpeakingStarted !== Modes.Translate
    ) {
      if (transcript.length > 0) {
        console.log("Final transcript to send with audio:", transcript);
        message.clientTranscription = transcript; // Send transcript to server with audio
      }
      // Set audio durations
      const recordingTime = recordingDuration - 1200; // Remove VAD delay by redemption frames at the end
      const maxRecordingDuration = Math.max(recordingTime, speakingDuration); // Caped recording duration to speaking duration
      message.recordingDuration = maxRecordingDuration;
      message.speakingDuration = speakingDuration;

      sendWebsocketMessage(message, accumulatedAudio);
      clear(); // Reset real time transcript
      resetAudioTimer(); // Reset audio timer after sending message
    }
  }, [audioReadyCounter]); // FIXED: Now uses counter instead of boolean toggle

  // Send message to reset mode
  const resetMode = (action: Action) => {
    console.log("resetMode called with action:", action);
    clear(); // Reset real time transcript
    resetVoiceChatState(); // Clear side panel and reset state via reducer

    const message = buildMessage(
      Role.User,
      action,
      voiceChatState.mode.modeStr,
      getLanguageByKey(i18n.language).name,
    );

    message.language = languageProficiency.language.name;
    sendWebsocketMessage(message, undefined);
  };

  // Reset mode on mount to trigger setup prompt
  // Reset mode also when mode or language learn changes
  useEffect(() => {
    console.log("Resetting mode due to mode or language change:", {
      mode: voiceChatState.mode.modeStr,
      languageProficiency: languageProficiency.language.name,
      websocketReady: websocketRef.current?.readyState,
    });

    if (
      !websocketRef.current ||
      websocketRef.current.readyState !== WebSocket.OPEN
    ) {
      return;
    }
    resetMode(Action.ChangeMode);
  }, [
    voiceChatState.mode,
    languageProficiency,
    websocketRef.current?.readyState,
  ]);

  // Handle Mic state
  const toggleMute = () => {
    setIsMuted((prev) => {
      const newState = !prev;
      if (newState) {
        resetAudioTimer();
        pause("all");
        clear();
      } else {
        start("all");
      }
      return newState;
    });
  };

  useEffect(() => {
    // Resume conversation mic state
    if (voiceChatState.translationPanel.isOpen) {
      // Translation panel is open - pause webspeech, keep VAD running
      pause("webspeech");
      start("vad");
    } else {
      // Translation panel is closed - restore normal state
      setAudioAccumulation(true);
      if (!isMuted) {
        start("all");
      } else {
        pause("all");
      }
    }

    // Enable mic toggle with keyboard
    const handleKeyDown = (event: KeyboardEvent) => {
      if (voiceChatState.translationPanel.isOpen) return;
      const tag = (event.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if ((event.target as HTMLElement)?.closest?.('[role="dialog"]')) return;
      if (event.code === "Space") {
        event.preventDefault();
        // When translation panel is closed, space bar toggles mic
        toggleMute();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [voiceChatState.translationPanel.isOpen, isMuted]);

  // Render component
  return (
    <div
      className="
          flex flex-col h-full max-w-screen mt-12 pb-0
          md:pb-12 md:flex-row md:justify-between md:items-stretch md:pt-2 md:pb-0"
    >
      <div className="absolute">
        <div className="p-4 pb-20 min-h-[500px]">
          <TranslationPanel
            session={session}
            sourceLang={getLanguageByKey(i18n.language)}
            targetLang={languageProficiency.language}
            setIsPanelOpen={(isOpen: boolean) => toggleTranslationPanel(isOpen)}
            isUserSpeaking={isAudioDetected}
            audioReadyCounter={audioReadyCounter}
            newMessage={voiceChatState.translationPanel.message}
          />
        </div>
      </div>
      <div className="mt-2 flex flex-col md:flex-auto items-center justify-stretch p-2 md:basis-2/5 min-w-0">
        <div className="w-full min-w-0">
          <CarouselModeComponent
            mode={voiceChatState.mode}
            setMode={selectMode}
          />
        </div>
        <ModeContextCard
          explanation={voiceChatState.modeContext}
          mode={voiceChatState.mode}
          restartMode={() => resetMode(Action.ResetMode)}
        />
      </div>
      {/* h-full  */}
      <div className="flex-col flex-auto md:basis-3/5 p-2 md:p-4 min-w-0">
        <ModeSidePanelComponent
          mode={voiceChatState.mode}
          languageProficiency={languageProficiency}
          newMessage={voiceChatState.currentMessage}
          transcript={transcript}
          interimTranscript={interimTranscript}
          setAudioPlaying={setIsResponsePlaying}
          sendWsMessage={(message: VoiceChatMessage) =>
            sendWebsocketMessage(message, undefined)
          }
          restartMode={() => resetMode(Action.ResetMode)}
        />
        {/* For mic toggle button on desktop screens */}
        <div className="relative">
          <div
            className="
              fixed bottom-6 left-1/2 transform -translate-x-1/2 translate-y-1
              md:inline md:absolute md:bottom-0 md:-left-12 md:translate-x-0 md:translate-y-4
            "
          >
            <MicButtonComponent
              isMuted={isMuted}
              audioDetected={isAudioDetected}
              disabled={
                isLoading ||
                voiceChatState.translationPanel.isOpen ||
                isResponsePlaying
              }
              onClick={toggleMute}
              size="md"
              tourKey="mic-button"
            />
            <div className="hidden md:inline absolute -bottom-4 left-1/2 transform -translate-x-1/2 text-xs text-foreground/80 space-x-1">
              <KeyHelper keyName={t("pages:voicechat.spaceBar")} />
            </div>
          </div>
        </div>
      </div>
      <TooltipTextTranslate
        sourceLanguage={languageProficiency.language}
        targetLanguage={getLanguageByKey(i18n.language)}
      />
    </div>
  );
}
